/******/ (() => { // webpackBootstrap
/******/ 	"use strict";
class GlobalDrawer extends HTMLElement {
  constructor() {
    super();
    this.closeElements = this.querySelectorAll('[data-drawer-close]');
    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.closeAllDrawers = this.closeAllDrawers.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-drawer'});
    window.eventBus.on('close:all:drawers', this.closeAllDrawers);
    this.closeElements.forEach((element) => element.addEventListener('click', this.closeDrawer));
    if (window.Shopify.designMode) window.addEventListener('resize', this.handleResize);
  }

  openDrawer() {
    window.eventBus.emit('close:all:drawers', this);
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('js-drawer-open');
    this.addEventListener('transitionend', this.handleTransitionEnd);
    window.eventBus.emit('drawer:opened', this);
  }

  closeDrawer() {
    this.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.global-drawer[aria-hidden="false"]')) {
      document.body.classList.remove('js-drawer-open');
    }
    this.removeEventListener('transitionend', this.handleTransitionEnd);
    window.eventBus.emit('drawer:closed', this);
  }

  closeAllDrawers(source) {
    if (source === this) return;
    this.closeDrawer();
  }

  handleTransitionEnd(event) {
    if (event.target === this) this.trapFocus();
  }

  trapFocus() {}

  handleResize() {
    this.classList.add('disable-transitions');
    setTimeout(() => this.classList.remove('disable-transitions'), 500);
  }

  disconnectedCallback() {
    window.eventBus?.off('close:all:drawers', this.closeAllDrawers);
    this.closeElements.forEach((element) => element.removeEventListener('click', this.closeDrawer));
    this.removeEventListener('transitionend', this.handleTransitionEnd);
    if (window.Shopify.designMode) window.removeEventListener('resize', this.handleResize);
  }
}

class CartDrawer extends GlobalDrawer {
  constructor() {
    super();
    this.dynamicContentIds = ['cart-header', 'cart-body', 'cart-footer'];
    this.scrollElement = this.querySelector('[data-cart-scroll-element]');
    this.statusRegion = this.querySelector('[data-cart-live-region]');
    this.backgroundInertState = new Map();
    this.opener = null;
    this.pendingOpener = null;
    this.pendingFocusState = null;
    this.pendingFocusTimeout = null;
    this.statusFrame = null;
    this.statusToken = 0;
    this.cartUpdateOpener = null;
    this.quickViewLauncher = null;
    this.openFocusToken = 0;
    this.handleCartOpen = this.handleCartOpen.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleCartRender = this.handleCartRender.bind(this);
    this.handleTabKeydown = this.handleTabKeydown.bind(this);
    this.handleCartUpdateToggle = this.handleCartUpdateToggle.bind(this);
    this.handleCartUpdateEscape = this.handleCartUpdateEscape.bind(this);
    this.handleFocusChange = this.handleFocusChange.bind(this);
    this.capturePotentialOpener = this.capturePotentialOpener.bind(this);
    this.captureFocusForUpdate = this.captureFocusForUpdate.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    window.wetheme.webcomponentRegistry.register({key: 'component-cart-drawer'});
    window.eventBus.on('open:cart:drawer', this.handleCartOpen);
    window.eventBus.on('update:cart:drawer', this.handleCartUpdate);
    window.eventBus.on('render:cart:drawer', this.handleCartRender);
    this.addEventListener('keydown', this.handleTabKeydown, true);
    this.addEventListener('click', this.handleCartUpdateToggle);
    this.addEventListener('keyup', this.handleCartUpdateEscape, true);
    this.addEventListener('focusin', this.handleFocusChange, true);
    this.addEventListener('click', this.captureFocusForUpdate, true);
    this.addEventListener('change', this.captureFocusForUpdate, true);
    document.addEventListener('pointerdown', this.capturePotentialOpener, true);
    document.addEventListener('click', this.capturePotentialOpener, true);
    document.addEventListener('keydown', this.capturePotentialOpener, true);
    document.addEventListener('submit', this.capturePotentialOpener, true);
    this.syncLauncherState(false);
  }

  disconnectedCallback() {
    if (this.isOpen()) this.closeDrawer({restoreFocus: false});
    else this.syncLauncherState(false);
    super.disconnectedCallback();
    window.eventBus?.off('open:cart:drawer', this.handleCartOpen);
    window.eventBus?.off('update:cart:drawer', this.handleCartUpdate);
    window.eventBus?.off('render:cart:drawer', this.handleCartRender);
    this.removeEventListener('keydown', this.handleTabKeydown, true);
    this.removeEventListener('click', this.handleCartUpdateToggle);
    this.removeEventListener('keyup', this.handleCartUpdateEscape, true);
    this.removeEventListener('focusin', this.handleFocusChange, true);
    this.removeEventListener('click', this.captureFocusForUpdate, true);
    this.removeEventListener('change', this.captureFocusForUpdate, true);
    document.removeEventListener('pointerdown', this.capturePotentialOpener, true);
    document.removeEventListener('click', this.capturePotentialOpener, true);
    document.removeEventListener('keydown', this.capturePotentialOpener, true);
    document.removeEventListener('submit', this.capturePotentialOpener, true);
    this.restoreBackground();
    this.clearPendingFocusState();
    if (this.statusFrame) cancelAnimationFrame(this.statusFrame);
  }

  handleCartOpen(event) {
    if (event?.scrollToTop && this.scrollElement) this.scrollElement.scrollTo(0, 0);
    this.openDrawer();
  }

  openDrawer() {
    const wasOpen = this.isOpen();
    const openFocusToken = !wasOpen ? ++this.openFocusToken : this.openFocusToken;
    if (!wasOpen) this.opener = this.consumeOpener();
    else this.pendingOpener = null;
    super.openDrawer();
    this.syncLauncherState(true);
    this.inertBackground();
    if (!wasOpen) this.focusDialogLabel(openFocusToken);
  }

  closeDrawer(options = {}) {
    if (!this.isOpen()) return;
    this.openFocusToken += 1;
    const restoreFocus = options.restoreFocus !== false;
    super.closeDrawer();
    this.restoreBackground();
    this.syncLauncherState(false);
    this.clearPendingFocusState();
    this.clearCartStatus();
    if (restoreFocus) this.restoreOpenerFocus();
    this.opener = null;
  }

  closeAllDrawers(source) {
    if (source === this) return;
    this.closeDrawer({restoreFocus: !source});
  }

  trapFocus() {
    this.ensureInitialFocus(this.openFocusToken);
  }

  handleCartUpdate(event) {
    if (event?.sections?.['cart-drawer']) this.renderCartDrawer(event.sections['cart-drawer']);
  }

  renderCartDrawer(html) {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const focusState = this.getFocusState() || this.pendingFocusState;
    this.clearPendingFocusState();

    this.dynamicContentIds.forEach((id) => {
      const selector = `[data-cart-dynamic-content="${id}"]`;
      const updatedContent = documentFragment.querySelector(selector)?.innerHTML;
      const targetElement = this.querySelector(selector);
      if (targetElement && updatedContent !== undefined) targetElement.innerHTML = updatedContent;
    });

    this.scrollElement = this.querySelector('[data-cart-scroll-element]');
    this.statusRegion = this.querySelector('[data-cart-live-region]');
    if (!this.isOpen()) {
      this.clearCartStatus();
      return;
    }

    this.restoreEquivalentFocus(focusState);
    this.announceCartUpdate(documentFragment);
  }

  async handleCartRender() {
    try {
      const response = await fetch(window.Shopify.routes.root + '?sections=cart-drawer');
      const responseJson = await response.json();
      if (responseJson?.['cart-drawer']) this.renderCartDrawer(responseJson['cart-drawer']);
    } catch (error) {
      console.error('Failed to fetch cart drawer:', error);
    }
  }

  handleTabKeydown(event) {
    if (event.key !== 'Tab' || !this.isOpen()) return;
    const focusableElements = this.getFocusableElements();
    event.stopPropagation();
    if (!focusableElements.length) {
      event.preventDefault();
      return;
    }

    const currentIndex = focusableElements.indexOf(document.activeElement);
    const nextIndex = currentIndex === -1
      ? (event.shiftKey ? focusableElements.length - 1 : 0)
      : (currentIndex + (event.shiftKey ? -1 : 1) + focusableElements.length) % focusableElements.length;
    event.preventDefault();
    this.focusElement(focusableElements[nextIndex]);
  }

  getFocusableElements() {
    const selector = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const activeModal = this.querySelector('[data-cart-update-modal][aria-hidden="false"]');
    const scope = activeModal || this;
    return Array.from(scope.querySelectorAll(selector)).filter((element) => {
      if (element.closest('[inert], [aria-hidden="true"], [hidden]')) return false;
      if (element.hasAttribute('disabled') || Number(element.getAttribute('tabindex')) < 0) return false;
      const styles = window.getComputedStyle(element);
      return styles.display !== 'none' && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
    });
  }

  focusDialogLabel(openFocusToken) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.ensureInitialFocus(openFocusToken));
    });
  }

  ensureInitialFocus(openFocusToken) {
    if (openFocusToken !== this.openFocusToken || !this.isOpen() || this.contains(document.activeElement)) return;
    this.focusElement(this.querySelector('#CartDrawer-Heading'), {preventScroll: true});
  }

  focusElement(element, options = {}) {
    if (!(element instanceof HTMLElement) || !element.isConnected) return;
    try {
      element.focus(options);
    } catch (error) {
      element.focus();
    }
  }

  isOpen() {
    return this.getAttribute('aria-hidden') === 'false';
  }

  inertBackground() {
    if (this.backgroundInertState.size) return;
    Array.from(document.body.children).forEach((element) => {
      if (element === this || element.tagName === 'SCRIPT') return;
      this.backgroundInertState.set(element, {
        hadInert: element.hasAttribute('inert'),
        inertValue: element.getAttribute('inert')
      });
      element.setAttribute('inert', '');
    });
  }

  restoreBackground() {
    this.backgroundInertState.forEach((state, element) => {
      if (!element.isConnected) return;
      if (state.hadInert) {
        element.setAttribute('inert', state.inertValue ?? '');
      } else {
        element.removeAttribute('inert');
      }
    });
    this.backgroundInertState.clear();
  }

  capturePotentialOpener(event) {
    if (event.type === 'keydown' && !['Enter', ' ', 'Spacebar'].includes(event.key)) return;
    const eventTarget = event.target instanceof Element ? event.target : null;
    const submitter = event.submitter instanceof Element ? event.submitter : null;
    const modalButton = eventTarget?.closest('[data-modal-button]');
    if (modalButton instanceof HTMLElement && modalButton.isConnected) this.quickViewLauncher = modalButton;
    const candidate = submitter || eventTarget?.closest('[aria-controls="CartDrawer"], .js-drawer-open-right-link, [data-quick-add-button], [data-add-to-cart], button[name="add"], input[name="add"]');
    const form = eventTarget?.closest('form[action*="/cart/add"]');
    const opener = candidate || form?.querySelector('[type="submit"]');
    if (!(opener instanceof HTMLElement) || !opener.isConnected) return;
    this.pendingOpener = {
      element: opener,
      quickViewFallback: opener.closest('#ThemeModal, [data-quick-view-product]') ? this.quickViewLauncher : null
    };
  }

  consumeOpener() {
    const candidate = this.pendingOpener;
    this.pendingOpener = null;
    if (this.isUsableFocusTarget(candidate?.element, {allowDisabled: true})) return candidate;

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement.matches('[aria-controls="CartDrawer"], .js-drawer-open-right-link')) {
      return {element: activeElement, quickViewFallback: null};
    }
    return null;
  }

  restoreOpenerFocus() {
    const opener = this.opener;
    requestAnimationFrame(() => {
      const anotherDialogIsOpen = document.querySelector('.global-drawer[aria-hidden="false"], [role="dialog"][aria-hidden="false"]');
      const target = this.isUsableFocusTarget(opener?.element)
        ? opener.element
        : opener?.quickViewFallback;
      if (!this.isOpen() && !anotherDialogIsOpen && this.isUsableFocusTarget(target)) {
        this.focusElement(target, {preventScroll: true});
      }
    });
  }

  isUsableFocusTarget(element, {allowDisabled = false} = {}) {
    if (!(element instanceof HTMLElement) || !element.isConnected || element.closest('[inert], [aria-hidden="true"], [hidden]')) return false;
    if (!allowDisabled && (element.hasAttribute('disabled') || Number(element.getAttribute('tabindex')) < 0)) return false;
    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  syncLauncherState(isExpanded) {
    document.querySelectorAll('[aria-controls="CartDrawer"], .js-drawer-open-right-link').forEach((launcher) => {
      launcher.setAttribute('aria-expanded', String(isExpanded));
    });
  }

  captureFocusForUpdate(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('[data-cart-quantity-input], [data-cart-quantity-button], [data-cart-remove-item]')) return;
    const focusState = this.getFocusState(target);
    if (!focusState) return;
    this.pendingFocusState = focusState;
    if (this.pendingFocusTimeout) clearTimeout(this.pendingFocusTimeout);
    this.pendingFocusTimeout = setTimeout(() => this.clearPendingFocusState(), 10000);
  }

  handleFocusChange(event) {
    if (!this.pendingFocusState || !(event.target instanceof Element)) return;
    const focusState = this.getFocusState(event.target);
    if (focusState) {
      this.pendingFocusState = focusState;
      return;
    }
    this.clearPendingFocusState();
  }

  handleCartUpdateToggle(event) {
    const target = event.target instanceof Element ? event.target : null;
    const updateModal = target?.closest('cart-update-modal');
    if (!updateModal) return;

    if (target.closest('[data-cart-update-open]')) {
      this.cartUpdateOpener = target.closest('[data-cart-update-open]');
      requestAnimationFrame(() => {
        if (!this.isOpen()) return;
        const modal = updateModal.querySelector('[data-cart-update-modal][aria-hidden="false"]');
        this.focusElement(modal?.querySelector('textarea, button:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      });
    }

    if (target.closest('[data-cart-update-close]')) {
      requestAnimationFrame(() => {
        if (this.isOpen() && updateModal.querySelector('[data-cart-update-modal]')?.getAttribute('aria-hidden') === 'true') {
          this.focusElement(this.cartUpdateOpener, {preventScroll: true});
        }
      });
    }
  }

  handleCartUpdateEscape(event) {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
    const updateModal = event.target.closest('cart-update-modal');
    if (!updateModal) return;
    requestAnimationFrame(() => {
      if (this.isOpen() && updateModal.querySelector('[data-cart-update-modal]')?.getAttribute('aria-hidden') === 'true') {
        this.focusElement(this.cartUpdateOpener, {preventScroll: true});
      }
    });
  }

  clearPendingFocusState() {
    this.pendingFocusState = null;
    if (this.pendingFocusTimeout) clearTimeout(this.pendingFocusTimeout);
    this.pendingFocusTimeout = null;
  }

  clearCartStatus() {
    this.statusToken += 1;
    if (this.statusFrame) cancelAnimationFrame(this.statusFrame);
    this.statusFrame = null;
    if (this.statusRegion) this.statusRegion.textContent = '';
  }

  getFocusState(element = document.activeElement) {
    if (!(element instanceof Element) || !this.contains(element)) return null;
    const stableControl = element.closest('[data-cart-focus-key], #CartDrawer-Heading, #CartDrawer-Checkout');
    if (stableControl) {
      return {
        control: 'stable',
        focusKey: stableControl.getAttribute('data-cart-focus-key') || stableControl.id
      };
    }
    const row = element.closest('[data-cart-line-key]');
    if (!row) return null;
    const rows = Array.from(this.querySelectorAll('[data-cart-line-key]'));
    const input = element.closest('[data-cart-quantity-input]');
    const quantityButton = element.closest('[data-cart-quantity-button]');
    const removeButton = element.closest('[data-cart-remove-item]');
    const productTitle = element.closest('[data-cart-product-title]');
    let control = 'row';
    if (input) control = 'quantity-input';
    if (quantityButton) control = `quantity-${quantityButton.getAttribute('name')}`;
    if (removeButton) control = 'remove';
    if (productTitle) control = 'title';

    return {
      lineKey: row.getAttribute('data-cart-line-key'),
      rowIndex: rows.indexOf(row),
      control
    };
  }

  restoreEquivalentFocus(focusState) {
    if (!focusState) return;
    if (focusState.control === 'stable') {
      const target = this.querySelector(`[data-cart-focus-key="${focusState.focusKey}"], #${focusState.focusKey}`);
      this.focusElement(target);
      return;
    }
    const rows = Array.from(this.querySelectorAll('[data-cart-line-key]'));
    let row = rows.find((item) => item.getAttribute('data-cart-line-key') === focusState.lineKey);
    const sameRowExists = Boolean(row);
    if (!row && rows.length) row = rows[Math.min(Math.max(focusState.rowIndex, 0), rows.length - 1)];

    if (!row) {
      this.focusElement(this.querySelector('#CartDrawer-Heading'));
      return;
    }

    let target = null;
    if (sameRowExists) {
      if (focusState.control === 'quantity-input') target = row.querySelector('[data-cart-quantity-input]');
      if (focusState.control === 'quantity-minus') target = row.querySelector('[data-cart-quantity-button][name="minus"]');
      if (focusState.control === 'quantity-plus') target = row.querySelector('[data-cart-quantity-button][name="plus"]');
      if (focusState.control === 'remove') target = row.querySelector('[data-cart-remove-item]');
      if (focusState.control === 'title') target = row.querySelector('[data-cart-product-title]');
    }
    if (!target) target = row.querySelector('[data-cart-product-title], [data-cart-quantity-input], a[href], button:not([disabled])');
    this.focusElement(target);
  }

  announceCartUpdate(documentFragment) {
    if (!this.statusRegion) return;
    const heading = documentFragment.querySelector('[data-cart-dynamic-content="cart-header"] .cart-drawer__heading')?.textContent?.trim();
    const subtotalHeading = documentFragment.querySelector('.cart-drawer__subtotal--heading')?.textContent?.trim();
    const subtotal = documentFragment.querySelector('.cart-drawer__subtotal--price')?.textContent?.trim();
    const status = [heading, subtotalHeading && subtotal ? `${subtotalHeading} ${subtotal}` : ''].filter(Boolean).join('. ');
    this.clearCartStatus();
    if (!status) return;
    const statusToken = this.statusToken;
    this.statusFrame = requestAnimationFrame(() => {
      this.statusFrame = null;
      if (statusToken === this.statusToken && this.isOpen() && this.statusRegion) this.statusRegion.textContent = status;
    });
  }
}

customElements.define('cart-drawer', CartDrawer);
/******/ })()
;
